<template>
  <DashboardLayout>
    <Breadcrumb class="flex breadcrumb" :model="items" />

    <!-- Event Details Popover -->
    <EventPopover :visible="showEventPopover" :event="selectedEvent" :isLocked="isEventLocked"
      :eventElement="eventElement" @close="closeEventPopover" />

    <div class="card flex flex-column justify-center pt-4 px-4">

      <div v-if="viewStack.length > 1" class="mb-4">
        <Button label="Back" icon="pi pi-arrow-left" class="p-button-text back-button cursor-pointer "
          @click="goBack" />
      </div>

      <div v-if="currentView.type === 'calendar'">
        <div class="calendar-header-row flex justify-content-between align-items-start gap-4 mb-3">
          <div class="flex justify-content-start">
            <Button label="Invite a student" icon="pi pi-user-plus" class="teal-button-small" @click="showInviteStudentDialog = true" />
          </div>

          <div class="calendar-status-panel flex flex-column gap-2">
          <!-- Connected state -->
          <Message v-if="isGoogleConnected" severity="success" :closable="false" class="m-0">
            <div class="flex align-items-center gap-3 min-w-0">
              <i class="pi pi-google font-bold"></i>
              <span>Google Account Connected</span>
              <Button
                label="Change"
                icon="pi pi-refresh"
                class="p-button-text p-button-sm p-button-danger md:ml-auto w-full md:w-auto"
                @click="confirmReset"
              />
            </div>
          </Message>

          <!-- Missing scopes error -->
          <Message v-else-if="calendarCallbackStatus === 'missing_scopes'" severity="error" :closable="false" class="m-0">
            <div class="flex align-items-center gap-3">
              <i class="pi pi-times-circle font-bold"></i>
              <div class="flex flex-column gap-1">
                <span class="font-semibold">Both permissions required</span>
                <span class="text-sm">You must grant Calendar and Email access. Please try again.</span>
              </div>
              <Button
                label="Try Again"
                icon="pi pi-refresh"
                class="p-button-text p-button-sm ml-auto"
                @click="connectCalendar"
              />
            </div>
          </Message>

          <!-- Generic error -->
          <Message v-else-if="calendarCallbackStatus === 'error'" severity="error" :closable="false" class="m-0">
            <div class="flex align-items-center gap-3">
              <i class="pi pi-exclamation-circle font-bold"></i>
              <span class="font-medium">Connection failed. Please try again.</span>
              <Button
                label="Try Again"
                icon="pi pi-refresh"
                class="p-button-text p-button-sm ml-auto"
                @click="connectCalendar"
              />
            </div>
          </Message>

          <!-- Not connected state -->
          <Message v-else severity="warn" :closable="false" class="m-0">
            <div class="flex align-items-center gap-3">
              <i class="pi pi-exclamation-triangle"></i>
              <span class="font-medium">Google Account Required</span>
              <Button
                label="Connect"
                icon="pi pi-external-link"
                class="p-button-text p-button-sm ml-auto"
                @click="showCalendarConnectionDialog = true"
              />
            </div>
          </Message>
          </div>
        </div>
        <CalendarView :calendarOptions="calendarOptions" @set-availability="pushView('availability', $event)"
          @schedule-class="pushView('schedule', $event)" />
      </div>
      <div v-if="currentView.type === 'availability'">
        <SetAvailability :availabilityList="availabilityList" @cancel="goBack" @add-availability="handleAddAvailability"
          @delete-availability="handleDeleteAvailability" />
      </div>
      <div v-if="currentView.type === 'schedule'">
        <ScheduleClass :request="selectedStudent" :enrolledStudents="enrolledStudents"
          :conflicts="scheduleConflicts" :unregistered-students="unregisteredStudents"
          @submit-request="submitSchedule" />
      </div>
    </div>

    <Dialog v-model:visible="showStudentSelectionDialog" modal
      :header="enrolledStudents && enrolledStudents.length > 0 ? 'Select a student to schedule a class' : 'Direct schedule not available'"
      :style="{ width: '50vw' }" :breakpoints="{ '960px': '75vw', '641px': '100vw' }">
      <div class="p-fluid" v-if="enrolledStudents && enrolledStudents.length > 0">
        <DataTable :value="enrolledStudents" v-model:selection="selectedStudent" selectionMode="single"
          dataKey="studentId" responsiveLayout="scroll" stripedRows class="p-datatable-sm">
          <Column selectionMode="single" headerStyle="width: 3em"></Column>

          <Column header="Student" style="min-width: 150px">
            <template #body="{ data }">
              <div class="flex align-items-center">
                <Image :src="getProfileImageUrl(data.studentPhotoURL)" alt="Student Photo" width="50"
                  imageClass="border-circle" preview />
                <span class="ml-3 font-medium">{{ data.studentName }}</span>
              </div>
            </template>
          </Column>

          <Column field="grade" header="Grade" sortable style="min-width: 100px">
            <template #body="{ data }">
              {{ cleanupLables(data.grade) }}
            </template>
          </Column>
          <Column header="Subjects" style="min-width: 150px">
            <template #body="{ data }">
              <span v-for="(subject, index) in data.subjects" :key="index">
                {{ cleanupLables(subject) }}{{ index < data.subjects.length - 1 ? ', ' : '' }} </span>
            </template>
          </Column>
        </DataTable>
      </div>
      <div v-else class="p-fluid">
        <div class="">
          <span class="Caption text-gray-700 text-center"> You don't have any enrolled students yet,<br />
            please wait for a class request in order to schedule a class.</span>
        </div>
      </div>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times" text @click="showStudentSelectionDialog = false"
          class="p-button-secondary" />
        <Button label="Schedule Class" icon="pi pi-calendar-plus" @click="selectStudentAndSchedule"
          :disabled="!selectedStudent" autofocus />
      </template>
    </Dialog>

    <InviteStudentDialog v-model:visible="showInviteStudentDialog" />

    <Dialog v-model:visible="showCalendarConnectionDialog" modal :closable="false" :header="'Connect Your Google Calendar'"
      :style="{ width: '50vw' }" :breakpoints="{ '960px': '75vw', '641px': '100vw' }">
      <div class="p-fluid">
        <Message v-if="calendarCallbackStatus === 'missing_scopes'" severity="error" :closable="false" class="mt-1 mb-3">
          <span class="Caption" style="color: inherit;">You must grant both Calendar and Email access. Please try again and accept all permissions.</span>
        </Message>
        <Message v-else-if="calendarCallbackStatus === 'error'" severity="error" :closable="false" class="mt-1 mb-3">
          <span class="Caption" style="color: inherit;">Something went wrong. Please try again.</span>
        </Message>
        <div class="">
          <span class="Caption text-gray-700 text-center">
            Connecting your google calendar will allow us to generate google meetings for your online classes.
          </span>
        </div>
      </div>
      <template #footer>
        <Button label="Connect Now" icon="pi pi-calendar-plus" @click="connectCalendar" autofocus />
      </template>
    </Dialog>

    <Dialog 
  v-model:visible="showConfirmClassesDialog" 
  modal 
  header="Confirm Completed Classes" 
  :style="{ width: '35vw', maxWidth: '500px' }" 
  :breakpoints="{ '1200px': '45vw', '960px': '60vw', '641px': '95vw' }"
>
  <div class="p-fluid pr-1" style="max-height: 60vh; overflow-y: auto;">
    <p class="mb-4 text-sm text-gray-600">
      The following sessions have ended. Please confirm them to finalize records.
    </p>

    <div v-for="session in unconfirmedClasses" :key="session._id" 
         class="session-card border-1 border-round surface-border p-3 mb-3 shadow-1">
      
      <div class="flex justify-content-between align-items-start mb-2">
        <div class="flex align-items-center gap-2">
          <Avatar :image="session.studentId.profilePhotoURL" shape="circle" size="normal" />
          <div>
            <div class="font-bold text-base line-height-2">{{ cleanupLables(session.classId?.subject) }}</div>
            <div class="text-xs text-gray-500">{{ session.studentId.username }}</div>
          </div>
        </div>
        <Tag :value="session.classId.classModel.replace(/_/g, ' ')" severity="info" style="font-size: 0.7rem" />
      </div>

      <div class="surface-100 p-2 border-round text-xs mb-3">
        <div class="flex align-items-center mb-1">
          <i class="pi pi-calendar mr-2 text-primary"></i>
          <span>{{ formatDate(session.date) }}</span>
        </div>
        <div class="flex align-items-center">
          <i class="pi pi-clock mr-2 text-primary"></i>
          <span>{{ formatTime(session.start) }} - {{ formatTime(session.end) }}</span>
        </div>
      </div>

      <Button 
        :label="session.classConfirmedByTutor ? 'Confirmed' : 'Mark as Completed'" 
        :icon="session.classConfirmedByTutor ? 'pi pi-check' : 'pi pi-check-circle'" 
        :class="session.classConfirmedByTutor ? 'p-button-secondary' : 'teal-button-small'"
        :disabled="session.classConfirmedByTutor"
        class="p-button-sm w-full" 
        @click="confirmClass(session)" 
      />
    </div>
  </div>

  <template #footer>
    <Button label="Close" icon="pi pi-times" @click="showConfirmClassesDialog = false" class="p-button-text p-button-sm" />
  </template>
</Dialog>

    <Dialog
      v-model:visible="showPaymentDialog"
      modal
      header="Payment Pending"
      :style="{ width: '35vw', maxWidth: '500px' }"
      :breakpoints="{ '1200px': '45vw', '960px': '60vw', '641px': '95vw' }"
    >
      <div class="p-fluid">
        <p class="mb-4 text-sm text-gray-600">
          The student hasn't processed the payment for this class yet. Would you like to try acquiring the payment now?
        </p>
      </div>
      <template #footer>
        <Button label="Cancel" icon="pi pi-times" @click="showPaymentDialog = false" class="p-button-text p-button-sm p-button-secondary" :disabled="isProcessingPayment" />
        <Button label="Acquire Payment" icon="pi pi-credit-card" @click="acquirePayment" :loading="isProcessingPayment" class="p-button-sm" autofocus />
      </template>
    </Dialog>
<ConfirmDialog :style="{ width: '50vw' }" :breakpoints="{ '960px': '75vw', '641px': '90vw' }" />
  </DashboardLayout>
</template>

<script setup>
import Breadcrumb from 'primevue/breadcrumb';
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useToast } from "primevue/usetoast";
import DashboardLayout from "@/layout/DashboardLayout.vue";
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import Button from 'primevue/button';
// PrimeVue Dialog/Table Imports
import Dialog from 'primevue/dialog';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Image from 'primevue/image';

// Local Component Imports
import CalendarView from "@/components/dashboard/tutor/tutorDashboard/calendarView.vue";
import EventPopover, { useEventPopover } from "@/components/calendarEventpopover.vue";
import InviteStudentDialog from "@/components/InviteStudentDialog.vue";
import SetAvailability from "@/components/dashboard/tutor/tutorDashboard/setAvailability.vue";
import ScheduleClass from "@/components/dashboard/tutor/tutorDashboard/scheduleClass.vue";
import blankProfile from '@/assets/blank-profile.png';
// API Import
import * as api from '@/api/tutor';
import { generateSignedUrl } from '@/api/storage';
import payments from "@/api/payments";

import { useAuthStore } from "@/stores/authStore";
import { notifyContrast, notifyError, notifySuccess, notifyWarn } from "@/utils/notification";
import { cleanupLables } from "@/utils/cleanups";
import { unregisteredEmailsFromError } from "@/utils/otherStudents";
import { useRoute } from 'vue-router';
import { ConfirmDialog, Message, useConfirm } from 'primevue';

const route = useRoute();
const authStore = useAuthStore();
const toast = useToast();
const scheduleConflicts = ref({ message: '', items: [] });
const unregisteredStudents = ref([]);
const availabilityList = ref([]);
const isGoogleConnected = ref(false);
const showCalendarConnectionDialog = ref(false);
const showInviteStudentDialog = ref(false);
const calendarCallbackStatus = ref(null); // 'missing_scopes' | 'error' | null
const showConfirmClassesDialog = ref(false);
const unconfirmedClasses = ref([]);
const showPaymentDialog = ref(false);
const selectedUnpaidSession = ref(null);
const isProcessingPayment = ref(false);
const imageUrls = ref({});

const confirm = useConfirm();

const {
  showEventPopover,
  selectedEvent,
  isEventLocked,
  eventElement,
  handleEventClick,
  handleEventMouseEnter,
  handleEventMouseLeave,
  closeEventPopover,
  cleanup
} = useEventPopover();

const getProfileImageUrl = (filePath) => {
  if (!filePath) return blankProfile;
  if (imageUrls.value[filePath]) return imageUrls.value[filePath];
  imageUrls.value[filePath] = blankProfile;

  generateSignedUrl(filePath)
    .then(url => {
      imageUrls.value[filePath] = url;
    })
    .catch(() => { });
  return blankProfile;
};

// --- UTILITY FUNCTIONS ---

/**
 * Processes raw class schedule data into a structured list for the calendar,
 * applying color coding based on the class's time relative to the current date.
 * @param {Array<Object>} rawData - List of raw class schedule objects.
 * @returns {Array<Object>} List of processed schedule events with colors.
 */
const processScheduleData = (rawData) => {
  // --- Configuration: Color Palette ---
  const COLORS = {
    PASSED: { backgroundColor: '#EF9A9A', borderColor: '#E57373', textColor: '#C62828' },
    NEXT: { backgroundColor: '#A5D6A7', borderColor: '#81C784', textColor: '#388E3C' },
    FUTURE: { backgroundColor: '#90CAF9', borderColor: '#64B5F6', textColor: '#1565C0' }
  };

  const now = new Date();

  // Convert UTC to IST floating datetime (no Z). IST = UTC+5:30 = +330 min.
  const toISTISO = (utcString) => {
    const date = new Date(utcString);
    const ist = new Date(date.getTime() + 330 * 60000);
    return ist.toISOString().slice(0, 19);
  };

  // When slotMaxTime > "24:00:00", FullCalendar places events whose IST time
  // falls before slotMinTime into the *previous* day's overflow column.
  // Fix: shift such events +1 calendar day so FullCalendar puts them in the
  // column whose header matches the correct IST date.
  const slotMinMinutes = Math.max(0, new Date().getHours() - 1) * 60;

  const shiftDateByOne = (istISO) => {
    const [datePart, time] = istISO.split('T');
    const [y, mo, d] = datePart.split('-').map(Number);
    const shifted = new Date(Date.UTC(y, mo - 1, d + 1));
    return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-${String(shifted.getUTCDate()).padStart(2, '0')}T${time}`;
  };

  const fixOverflow = (startIST, endIST) => {
    const [sh, sm] = startIST.split('T')[1].split(':').map(Number);
    const [eh, em] = endIST.split('T')[1].split(':').map(Number);
    const startMins = sh * 60 + sm;
    const endMins = eh * 60 + em;

    if (startMins < slotMinMinutes) {
      if (endMins > slotMinMinutes) {
        // Event spans slotMinTime boundary — clamp start to slotMinTime on same
        // date so the event stays in the correct day column without wrapping.
        const datePart = startIST.split('T')[0];
        const clampedH = String(Math.floor(slotMinMinutes / 60)).padStart(2, '0');
        const clampedM = String(slotMinMinutes % 60).padStart(2, '0');
        return { start: `${datePart}T${clampedH}:${clampedM}:00`, end: endIST };
      }
      // Entire event is before slotMinTime — shift +1 day into correct column.
      return { start: shiftDateByOne(startIST), end: shiftDateByOne(endIST) };
    }
    return { start: startIST, end: endIST };
  };

  const sortedClasses = rawData
    .map(item => ({
      ...item,
      startDate: new Date(item.start)
    }))
    .sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

  const futureClasses = sortedClasses.filter(item => item.startDate.getTime() > now.getTime());
  const nextClassStart = futureClasses.length > 0 ? futureClasses[0].startDate.getTime() : null;

  const processedEvents = sortedClasses.map(classItem => {
    const classStartTimestamp = classItem.startDate.getTime();
    let colorProfile = COLORS.FUTURE;

    if (classStartTimestamp < now.getTime()) {
      colorProfile = COLORS.PASSED;
    } else if (classStartTimestamp === nextClassStart) {
      colorProfile = COLORS.NEXT;
    }

    const subjectTitle = classItem.subject ? classItem.subject.charAt(0).toUpperCase() + classItem.subject.slice(1) : 'Unknown';

    const startIST = toISTISO(classItem.start);
    const endIST = toISTISO(classItem.end);
    const { start, end } = fixOverflow(startIST, endIST);

    return {
      title: subjectTitle,
      start,
      end,
      extendedProps: {
        classType: classItem.classType,
        classModel: classItem.classModel,
        location: classItem.classLocation || '',
        with: classItem.student || '',
        tutor: classItem.tutor || 'N/A',
        note: classItem.note || '',
        groupClass: classItem.groupClass || false,
        otherStudents: classItem.otherStudents,
        classLink: classItem.classLink || '',
        displayStart: startIST,
        displayEnd: endIST,
      },
      ...colorProfile
    };
  });

  return processedEvents;
};



// --- REACTIVE STATE ---
const getDynamicScrollTime = () => {
  const now = new Date();
  let hour = now.getHours();
  let targetHour = Math.max(0, hour - 1);
  return targetHour.toString().padStart(2, '0') + ':00:00';
};

const getDynamicMaxTime = () => {
  const now = new Date();
  let hour = now.getHours();
  let targetHour = Math.max(0, hour - 1);
  return (targetHour + 24).toString() + ':00:00';
};
const calendarOptions = ref({
  plugins: [dayGridPlugin, timeGridPlugin],
  initialView: window.innerWidth < 1024 ? 'timeGridDay' : 'timeGridWeek',
  headerToolbar: {
    left: 'title',
    center: '',
    right: 'prev,today,next',
  },
  titleFormat: {
    year: 'numeric',
    month: 'long'
  },
  height: 'auto',
  contentHeight: 'auto',
  aspectRatio: 1.5,
  handleWindowResize: true,
  windowResizeDelay: 100,
  windowResize: (arg) => {
    const calendar = arg.view.calendar;
    if (window.innerWidth < 1024 && arg.view.type !== 'timeGridDay') {
      calendar.changeView('timeGridDay');
    } else if (window.innerWidth >= 1024 && arg.view.type !== 'timeGridWeek') {
      calendar.changeView('timeGridWeek');
    }
  },
  expandRows: true,
  nowIndicator: true,
  slotMinTime: getDynamicScrollTime(),
  slotMaxTime: getDynamicMaxTime(),
  allDaySlot: false,
  dayHeaderFormat: { day: 'numeric', weekday: 'short' },
  // Initialize events as an empty array
  events: [],
  // Add event interaction handlers
  eventClick: handleEventClick,
  eventMouseEnter: handleEventMouseEnter,
  eventMouseLeave: handleEventMouseLeave,
  eventContent: function (arg) {
    // Access the custom data via arg.event.extendedProps
    const subject = cleanupLables(arg.event.title);
    const classType = arg.event.extendedProps.classType;
    const location = arg.event.extendedProps.location;
    const dispStart = new Date(arg.event.extendedProps.displayStart);
    const dispEnd = new Date(arg.event.extendedProps.displayEnd);
    const time = dispStart.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }) + ' - ' + dispEnd.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    const withWho = arg.event.extendedProps.with;
    const link = arg.event.extendedProps.classLink;

    // Create the custom HTML element
    let customHtml = document.createElement('div');
    customHtml.innerHTML = `
      <div class="fc-event-main-frame" style="text-align: center; padding-top: 20px">
        <div class="fc-event-title" style=font-weight:900>${subject}</div>
        <div class="event-description-snippet">${classType}</div>
        <div class="event-description-snippet">${location}</div>
        <div class="event-description-snippet">${time}</div>
        <div class="event-description-snippet" style="text-align: center"><b>${withWho}</b></div>
        ${link ? `<div class="event-description-snippet"><a href="${link}" target="_blank"><b>Meet Link</b></a></div>` : ''}
      </div>  
    `;

    return { domNodes: [customHtml] }
  }
});

const viewStack = ref([
  { type: "calendar", label: "Class Schedules", data: null }
]);

// --- NEW STATE FOR DIALOG ---
const showStudentSelectionDialog = ref(false);
const enrolledStudents = ref([]);
const selectedStudent = ref(null);

// --- COMPUTED PROPERTIES ---

const items = computed(() => {
  return [
    { label: "Dashboard" },
    ...viewStack.value.map(v => ({ label: v.label }))
  ];
});

const currentView = computed(() =>
  viewStack.value[viewStack.value.length - 1]
);

// --- METHODS ---

async function pushView(type, data) {
  if (type === "availability") {
    viewStack.value.push({ type, label: "Set My Availability", data });
  } else if (type === "schedule") {
    // 1. Fetch students
    try {
      const studentsResponse = await api.fetchEnrolledStudents();
      // Assuming the response structure is { data: { data: [...] } }
      enrolledStudents.value = studentsResponse.data.data;

      // 2. Clear any previous selection
      selectedStudent.value = null;

      // 3. Show the student selection dialog
      showStudentSelectionDialog.value = true;
    } catch (error) {
      console.error("Error fetching enrolled students:", error);
      // Handle error display to the user if needed
    }
  } else {
    viewStack.value.push({ type, label: "Request New Class", data });
  }
}

function selectStudentAndSchedule() {
  if (selectedStudent.value) {
    const st = {
      studentId: {
        username: selectedStudent.value.studentName,
        _id: selectedStudent.value.studentId
      },
      grade: selectedStudent.value.grade,
      tutorId: authStore.user?.userId
    }
    selectedStudent.value = st;
    viewStack.value.push({
      type: "schedule",
      label: `Schedule Class with ${selectedStudent.value.studentId.username}`,
      data: selectedStudent.value
    });
    // Close the dialog
    showStudentSelectionDialog.value = false;
  }
}

function goBack() {
  if (viewStack.value.length > 1) {
    viewStack.value.pop();
  }
}

const fetchSchedules = async () => {
  try {
    const res = await api.fetchSchedules();
    const rawData = res.data.data;
    const events = processScheduleData(rawData);
    calendarOptions.value.events = events;
  } catch (error) {
    console.error("Error fetching schedules:", error);
  }
}

const submitSchedule = async (data) => {
  scheduleConflicts.value = { message: '', items: [] };
  unregisteredStudents.value = [];
  try {
    notifyContrast("Please wait...", "We are processing your request.");
    await api.createScheduledClassRequest(data);
    toast.removeAllGroups();
    notifyContrast("Waiting for approval!", "We've notified the student. They'll review the schedule and give you a confirmation shortly.");
    viewStack.value.pop();
    await fetchSchedules();
  } catch (error) {
    toast.removeAllGroups();
    const unregisteredEmails = unregisteredEmailsFromError(error);
    if (error.response?.data?.code === 409) {
      scheduleConflicts.value = {
        message: error.response.data.message || '',
        items: error.response.data.data || []
      };
    } else if (error.response?.data?.code === 403) {
      notifyWarn("Plan Limit Reached", error.response.data.message);
    } else if (unregisteredEmails.length > 0) {
      unregisteredStudents.value = unregisteredEmails;
      notifyError("Unregistered students", error.response.data.message);
    } else {
      notifyError(error.response?.data?.status || "Error", error.response?.data?.message || "Failed to schedule class.");
    }
  }
}
const getAvailability = async () => {
  try {
    const res = await api.getAvailability(authStore.user?.userId);
    availabilityList.value = res.data.data;
  } catch (err) {
    console.error("Failed to load availability", err);
  }
}
async function handleAddAvailability(payload) {
  try {
    await api.addAvailability({
      tutorId: authStore.user?.userId,
      start: payload.start,
      end: payload.end,
      date: payload.date,
      note: payload.note
    });

    const res = await api.getAvailability(authStore.user?.userId);
    availabilityList.value = res.data.data;

  } catch (error) {
    console.error("Error adding availability:", error);
  }
}
async function handleDeleteAvailability(slotId) {
  try {
    await api.removeAvailability(slotId);

    const res = await getAvailability(authStore.user?.userId);
    availabilityList.value = res.data.data;
  } catch (err) {
    console.error("Error removing availability:", err);
  }
}

async function connectCalendar() {
  showCalendarConnectionDialog.value = false;
  const id = authStore.user.keycloakId;
  const res = await api.connectGoogleCalendar(id);
  location.replace(res.data.url);
}

const confirmReset = () => {
  confirm.require({
    header: 'Change Google Account?',
    message: 'A Google account is mandatory to set up meetings with students. Disconnecting will stop sync until you link a new account. Do you want to proceed?',
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Disconnect & Change',
    acceptClass: 'p-button-danger',
    accept: () => {
      resetConnection();
    }
  });
};

async function resetConnection() {
  try {
    const id = authStore.user.keycloakId;
    await api.resetGoogleCalendarConnection(id);
    if (!authStore.user.googleCalendar) authStore.user.googleCalendar = {};
    authStore.user.googleCalendar.connected = false;
    isGoogleConnected.value = false;
    await connectCalendar();
  } catch (error) {
    notifyError("Failed!", "Failed to reset connection. Please try again.");
  }
}

async function fetchUnconfirmedClasses() {
  try {
    const res = await api.getUnconfirmedClasses();
    unconfirmedClasses.value = res.data.data;
    if(unconfirmedClasses.value.length > 0) {
      showConfirmClassesDialog.value = true;
    }else{
       showConfirmClassesDialog.value = false;
    }
  } catch (error) {
    console.error("Error fetching unconfirmed classes:", error);
  }
}

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
};

const formatTime = (timeString) => {
  return new Date(timeString).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const confirmClass = async (session) => {
  // Check if we already know payment is pending locally
  const isPaymentPending = session.paymentStatus && !['completed', 'paid'].includes(session.paymentStatus.toLowerCase());

  if (isPaymentPending) {
    selectedUnpaidSession.value = session;
    showPaymentDialog.value = true;
    return;
  }

  try {
    const res = await api.updateClassConfirmationForPayment(session._id);
    if (res.data.success) {
      const classInstance = unconfirmedClasses.value.find(c => c._id === session._id);
      if (classInstance) {
        classInstance.classConfirmedByTutor = true;
        unconfirmedClasses.value = unconfirmedClasses.value.filter(c => c._id !== session._id);
        notifySuccess("Success", "Class confirmed successfully!");
      }
    }
  } catch (e) {
    // If the backend actively rejects the confirmation specifically due to a missing payment
    if (e.response?.status === 402 || e.response?.data?.message?.toLowerCase().includes('payment')) {
      selectedUnpaidSession.value = session;
      showPaymentDialog.value = true;
    } else {
      notifyError("Class Confirmation Failed!", e.response?.data?.message || "Please try again later!");
    }
  }
}

const acquirePayment = async () => {
  if (!selectedUnpaidSession.value) return;
  isProcessingPayment.value = true;
  try {
    const res = await payments.payForClass(selectedUnpaidSession.value._id);
    if (res.data && res.data.success) {
      notifySuccess("Payment Successful", "The payment has been successfully acquired.");
      showPaymentDialog.value = false;
      await confirmClass({ ...selectedUnpaidSession.value, paymentStatus: 'completed' });
    } else {
      notifyError("Payment Failed", "Payment could not be processed. Please wait until the student processes the payment or contact the student to resolve this issue.");
    }
  } catch (error) {
    notifyError("Payment Failed", "Payment could not be processed. Please wait until the student processes the payment or contact the student to resolve this issue.");
  } finally {
    isProcessingPayment.value = false;
  }
}

// --- LIFECYCLE HOOKS ---

onMounted(async () => {
  fetchSchedules();
  getAvailability();
  fetchUnconfirmedClasses();

  const calendarParam = route.query.calendar;
  if (calendarParam === 'success') {
    if (!authStore.user.googleCalendar) authStore.user.googleCalendar = {};
    authStore.user.googleCalendar.connected = true;
    isGoogleConnected.value = true;
    notifySuccess("Google Connected!", "Your Calendar and Gmail are now linked.");
  } else if (calendarParam === 'missing_scopes') {
    calendarCallbackStatus.value = 'missing_scopes';
    showCalendarConnectionDialog.value = true;
  } else if (calendarParam === 'error') {
    calendarCallbackStatus.value = 'error';
    showCalendarConnectionDialog.value = true;
  } else {
    try {
      const profileRes = await api.getProfile();
      isGoogleConnected.value = !!profileRes.data.data.googleCalendar?.connected;
      if (!isGoogleConnected.value) {
        showCalendarConnectionDialog.value = true;
      }
    } catch {
      showCalendarConnectionDialog.value = true;
    }
  }
});

onUnmounted(() => {
  cleanup();
});
</script>

<style>
.back-button {
  color: var(--gray-600) !important;
}

.back-button:hover {
  color: var(--gray-600) !important;
  background-color: var(--gray-200) !important;
}

.back-button:active {
  color: var(--gray-600) !important;
}

.breadcrumb {
  display: flex !important;
  flex-wrap: wrap !important;
  overflow-x: hidden !important;
}

.text-gray-700 {
  color: var(--gray-700) !important;
  padding-top: 30vh;
}

/* Custom styling for the student photo in the data table */
.border-circle {
  border-radius: 50%;
  object-fit: cover;
}

.calendar-status-panel {
  width: min(25vw, 520px);
  min-width: 260px;
}

.calendar-header-row {
  width: 100%;
}

@media (max-width: 767px) {
  .calendar-header-row {
    flex-direction: column;
  }

  .calendar-status-panel {
    width: 100%;
    min-width: 0;
  }
}
</style>